import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useSettings } from '../../context/SettingsContext';

function Icon({ name, className = 'h-5 w-5' }) {
  const paths = {
    mail: <><rect x="3" y="5" width="18" height="14" rx="1"/><path d="m3 7 9 6 9-6"/></>,
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c1 .4 1.9.6 2.9.7a2 2 0 0 1 1.7 2Z"/>,
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    arrow: <path d="M5 12h14m-5-5 5 5-5 5"/>,
  };
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

const emptyForm = { name: '', email: '', phone: '', topic: 'Order support', orderNumber: '', message: '' };

export default function ContactUs() {
  const { settings } = useSettings();
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const contact = settings?.contact || {};
  const email = contact.email || 'support@chandanaaura.com';
  const phone = contact.phone || '+91 98765 43210';
  const whatsapp = contact.whatsapp || phone;
  const address = contact.address || 'Main Temple Road, Mysuru, Karnataka 570001';

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Please enter your full name';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!form.message.trim()) {
      newErrors.message = 'Please enter your message or inquiry';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required fields');
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      setSubmitted(true);
      setForm(emptyForm);
      toast.success('Message sent to Chandana Aura support');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not send your message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-[#241914]">
      {/* Standardized Long-Width Panoramic Banner */}
      <section className="relative min-h-[220px] sm:min-h-[250px] lg:min-h-[270px] overflow-hidden border-b border-[#3A332E] flex items-end">
        <img
          src="/assets/contact_us_banner.jpg"
          alt="Chandana Aura customer hospitality and concierge desk"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="relative mx-auto flex min-h-[220px] sm:min-h-[250px] lg:min-h-[270px] max-w-7xl items-end px-5 pb-7 sm:pb-8 pt-12 text-white w-full z-10">
          <div className="max-w-2xl space-y-1.5">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-[#C49A45] block">
              Dedicated Customer Care & Concierge
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-white drop-shadow-md">
              Contact & Support
            </h1>
            <p className="max-w-xl text-xs sm:text-sm leading-relaxed text-[#EEE8E1] line-clamp-2 drop-shadow">
              Have questions about an order, custom temple carving, or certificate verification? Reach out to our team.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-[#DDD4CC] bg-[#F7F5F2]">
        <div className="mx-auto grid max-w-7xl sm:grid-cols-3 sm:px-6">
          {[
            ['mail', 'Email', email, `mailto:${email}`],
            ['phone', 'Call or WhatsApp', phone, `https://wa.me/${String(whatsapp).replace(/\D/g, '')}`],
            ['clock', 'Support hours', 'Monday to Saturday, 9 AM - 7 PM', null],
          ].map(([icon, label, value, href], index) => (
            <div key={label} className={`flex min-w-0 items-start gap-3 px-5 py-5 ${index < 2 ? 'border-b border-[#DDD4CC] sm:border-b-0 sm:border-r' : ''}`}>
              <Icon name={icon} className="mt-0.5 h-4 w-4 shrink-0 text-[#9D772D]" />
              <div className="min-w-0"><p className="text-[9px] font-bold uppercase text-[#76675E]">{label}</p>{href ? <a href={href} className="mt-1 block break-words text-xs font-semibold hover:text-[#9D772D]" target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{value}</a> : <p className="mt-1 text-xs font-semibold">{value}</p>}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-[.72fr_1.28fr] lg:gap-14">
        {/* Left Side: Artisan Support Visual Card, Address & Google Maps */}
        <aside className="space-y-6">
          {/* Visual Support Image Card */}
          <div className="relative overflow-hidden rounded-2xl border border-[#E0D6CC] shadow-md group">
            <img
              src="/assets/contact_support_card.jpg"
              alt="Chandana Aura artisan concierge and boutique support desk"
              className="h-52 sm:h-60 w-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#140E0B]/90 via-[#140E0B]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#D7B66D] block">
                Direct Artisan Concierge
              </span>
              <h3 className="font-heading text-xl font-bold mt-1 text-white">
                Mysuru Heritage Workshop
              </h3>
              <p className="text-xs text-[#E8DFD8] mt-1 line-clamp-2">
                Personalized care for temple sanctum statues, custom japa malas, and certificate verification.
              </p>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase text-[#9D772D]">Get in touch</p>
            <h2 className="mt-1 font-heading text-2xl font-semibold leading-tight sm:text-3xl">Helpful answers begin with a little context.</h2>
            <p className="mt-3 text-xs leading-relaxed text-[#665950]">Include your order number when your question concerns a purchase. For custom sacred carvings, mention the deity, wood grade, and dimensions.</p>
          </div>

          {/* Workshop Address & Interactive Google Maps */}
          <div className="border-t border-[#DDD4CC] pt-5">
            <div className="flex items-start gap-3">
              <Icon name="pin" className="mt-0.5 h-5 w-5 shrink-0 text-[#9D772D]"/>
              <div>
                <p className="text-[10px] font-bold uppercase text-[#76675E]">Workshop and correspondence</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#4F433C]">{address}</p>
              </div>
            </div>

            {/* Interactive Google Maps Embed */}
            <div className="mt-4 overflow-hidden rounded-xl border border-[#D9D0C8] shadow-sm">
              <iframe
                title="Chandana Aura Mysuru Location Map"
                src="https://maps.google.com/maps?q=Main+Temple+Road,+Mysuru,+Karnataka+570001&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="190"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
              <div className="bg-[#FAF8F5] px-3.5 py-2 border-t border-[#E8DFD8] flex items-center justify-between text-[11px]">
                <span className="text-[#6B5B50] font-medium truncate">{address}</span>
                <a
                  href="https://maps.google.com/?q=Main+Temple+Road,+Mysuru,+Karnataka+570001"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#9D772D] hover:underline font-bold uppercase text-[10px] shrink-0 ml-2"
                >
                  Open Google Maps ↗
                </a>
              </div>
            </div>
          </div>
        </aside>

        <div className="border border-[#D9D0C8] bg-[#FAF9F7] p-5 shadow-sm sm:p-7">
          {submitted ? (
            <div className="flex min-h-[440px] flex-col items-center justify-center text-center">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[#E7EEE9] text-[#42604A]"><Icon name="check" className="h-6 w-6"/></div>
              <h2 className="mt-5 font-heading text-2xl font-semibold">Message received</h2>
              <p className="mt-3 max-w-sm text-xs leading-5 text-[#665950]">Your request is now visible to our support team. We usually reply within one business day.</p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setErrors({});
                }}
                className="mt-6 border border-[#8E6B27] px-5 py-2.5 text-[10px] font-bold uppercase text-[#7F5D1E] hover:bg-[#F1E9DA]"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="border-b border-[#D9D0C8] pb-5">
                <p className="text-[10px] font-bold uppercase text-[#9D772D]">Support request</p>
                <h2 className="mt-2 font-heading text-2xl font-semibold">Send us a message</h2>
                <p className="mt-2 text-xs text-[#76675E]">Required fields are marked with an asterisk (*).</p>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    Full name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="e.g. Rajesh Sharma"
                    className={`input-field bg-white ${
                      errors.name ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                  />
                  {errors.name && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className={`input-field bg-white ${
                      errors.email ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                  />
                  {errors.email && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    Phone number
                  </label>
                  <input
                    type="tel"
                    autoComplete="tel"
                    placeholder="+91 98765 43210"
                    className="input-field bg-white"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="input-field bg-white"
                    value={form.topic}
                    onChange={(e) => update('topic', e.target.value)}
                  >
                    <option>Order support</option>
                    <option>Product guidance</option>
                    <option>Payment help</option>
                    <option>Returns and refunds</option>
                    <option>Wholesale or gifting</option>
                    <option>Other</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    Order number <span className="normal-case font-normal text-[#8A7B72]">(if applicable)</span>
                  </label>
                  <input
                    type="text"
                    className="input-field bg-white"
                    placeholder="Example: CA12345678"
                    value={form.orderNumber}
                    onChange={(e) => update('orderNumber', e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold uppercase text-[#5F5149] mb-1.5">
                    How can we help? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="6"
                    maxLength="2000"
                    placeholder="Describe your inquiry or order details..."
                    className={`input-field resize-y bg-white ${
                      errors.message ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                  />
                  {errors.message && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 border-t border-[#D9D0C8] pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[10px] leading-4 text-[#76675E]">Your details are used only to respond to this request.</p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="shrink-0 bg-[#2B1810] px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#9D772D] transition-colors disabled:opacity-60 rounded"
                >
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
